import type { GradingAcceptPath, GradingOutcome, GradingVerdict } from "@/constants/grading"
import type { MaterialOrigin } from "@/constants/materials"

export interface School {
  id: string
  name: string
  city: string
  state: string
  cct: string
  // PRD-47 v2 — admin-owned campus generation audience; read-only here.
  // Optional only for the deploy window (an older backend omits it).
  education_level?: EducationLevel
  // PRD-55 — co-branding crest, resolved through School → Institution
  // inheritance by the backend. null means "no logo": render the
  // Platonica chrome, never a substituted default.
  //
  // Optional for the same deploy-window reason as the line above, in
  // both directions: during the PR-1 → PR-3 rollout the SPA may run
  // against a backend that omits it, and after a frontend rollback
  // against one that sends it.
  logo_url?: string | null
}

export interface Professor {
  id: string
  email: string
  name: string
  full_name: string
  school: School
  semester: string
  initials: string
  color: "purple" | "blue" | "green" | "orange"
  settings: ProfessorSettings
  // PRD-38 / ADR-0032 — the academic config resolved for this professor's
  // school (School override when set, Institution value otherwise). Always
  // present on the payload; professors never choose these values.
  academic_config: AcademicConfig
  // PRD-22: server-computed from `core.email_policy.is_email_blocked_for`.
  // True iff this is an internal (`@platonica.io` / `@sumus.mx`) account AND
  // INTERNAL_ACCOUNT_EMAILS_ENABLED is False. Drives the AppShell
  // banner and truthful copy; real blocks happen server-side at the
  // dispatch sites. One client-side gate (PRD-73): the bulk «Dar más
  // tiempo» is hidden while it is true, because that sweep is email-only
  // and would reopen windows nobody is told about.
  // Optional so older cached responses parse during the deploy window;
  // treat undefined as "not internal" (matches backend default).
  emails_blocked?: boolean
  // PRD-22 §FR-18: server-computed from `core.email_policy.is_internal_account`
  // — the dev-tools predicate (an exact allowlist + the internal domains). The
  // SPA never holds the list; it gates the "Herramientas de desarrollo"
  // card on this boolean and nothing else. Optional for the same
  // deploy-window reason; undefined = not internal.
  is_internal_account?: boolean
  // PRD-22 §FR-17: WHY emails_blocked is true. "env_flag" = the environment
  // kill switch has the last word (the real-emails toggle renders disabled);
  // "user_toggle" = the professor switched it off in Settings. "ops_paused"
  // (PRD-22 §FR-19) = Platonica staff paused this account's emails from the
  // admin console — the only reason that applies to real teachers too.
  // null/undefined = nothing blocks.
  email_block_reason?: "env_flag" | "user_toggle" | "ops_paused" | null
  // PRD-13 follow-up: mirrors ``settings.MATERIAL_DELIVERY_ENABLED``.
  // The SendDecisionModal uses this PLUS emails_blocked to render
  // truthful copy (was previously hardcoded to "entrega simulada —
  // no se enviaron correos" regardless of env). Optional for the same
  // back-compat reason as emails_blocked.
  material_delivery_enabled?: boolean
}

export const GRADE_SCALES = ["0-10", "0-100"] as const
export type GradeScale = (typeof GRADE_SCALES)[number]
export const DEFAULT_GRADE_SCALE: GradeScale = "0-10"

// Mirrors apps/backend/core/models.py::Institution.PeriodType (PRD-38).
export const PERIOD_TYPES = ["anual", "semestral", "trimestral"] as const
export type PeriodType = (typeof PERIOD_TYPES)[number]

/**
 * Mirrors apps/backend/api/schemas.py::AcademicConfigOut (PRD-38 / ADR-0032).
 *
 * Tenant configuration, already RESOLVED by the backend: the School's
 * override when it has one, the Institution's value otherwise. `grade_scale`
 * moved here from `ProfessorSettings` — read it through `useGradeScale()`,
 * never off the raw settings blob. `periods_per_cycle` bounds the classroom
 * `period_number` (1..N); a cycle of 1 has nothing to choose.
 */
export interface AcademicConfig {
  period_type: PeriodType
  periods_per_cycle: number
  grade_scale: GradeScale
}

/**
 * Client-side fallback for a payload that predates PRD-38 (a login response
 * cached across the deploy). `periods_per_cycle` is deliberately 1 — NOT the
 * backend's `trimestral / 3` default: with 1 the Periodo control hides and we
 * submit `period_number: 1`, which every configuration accepts. Offering 2 or
 * 3 options we can't verify would risk a `period_number_out_of_range` 422.
 */
export const DEFAULT_ACADEMIC_CONFIG: AcademicConfig = {
  period_type: "trimestral",
  periods_per_cycle: 1,
  grade_scale: DEFAULT_GRADE_SCALE,
}

export interface ProfessorSettings {
  theme?: "system" | "light" | "dark"
  notifications_email?: boolean
  notifications_push?: boolean
  language?: "es-MX" | "en-US"
  onboarding_completed?: boolean
  onboarding_completed_at?: string
  // PRD-22 §FR-17 — the professor's own "send real emails" switch. Read by
  // `core.email_policy.wants_real_emails`; absent means ON. Only meaningful
  // for internal accounts (the server checks `is_internal_account` first),
  // so a non-internal professor carrying the key is inert, not blocked.
  dev_real_emails_enabled?: boolean
  // PRD-38 FR-24 — RETIRED as an input. The backend deliberately leaves the
  // stored key in place on existing professors (so the pre-migration value
  // survives for inspection), but it is ignored: the effective scale is
  // `Professor.academic_config.grade_scale`. Do not read this field.
  grade_scale?: GradeScale
  // PRD-17 §FR-4 — future role flag pre-baked into the type so the gate
  // predicate doesn't need a second migration when the backend ships
  // `professor.settings.is_director`. Today the gate falls back to the
  // `@platonica.io` email check when this is undefined.
  is_director?: boolean
}

export interface AuthTokens {
  access: string
  refresh: string
}

export interface LoginResponse extends AuthTokens {
  professor: Professor
}

// PRD-37 / ADR-0031 — school-scoped subject catalog. Mirrors
// apps/backend/api/schemas.py::SubjectOut. Subject is no longer a fixed
// backend enum; each school has its own active subject rows, fetched via
// GET /api/subjects/. `default_code` is set only for the ten seeded
// defaults (mirrors core/constants.py::CLASSROOM_DEFAULT_ICONS keys) and
// is null for any school-custom subject.
export interface Subject {
  id: string
  name: string
  default_code: string | null
}

// Canonical wire values from apps/backend/core/models.py::EducationLevel and
// the engine v11 material-types manifest. Missing legacy engine input defaults
// to highschool; the professor-facing Classroom forms always send one.
export const EDUCATION_LEVELS = ["highschool", "university"] as const
export type EducationLevel = (typeof EDUCATION_LEVELS)[number]

// PRD-48 — education-relative adaptive learner state. The numeric value is
// professor-only in v1 and never belongs on a student-facing surface.
export type AdaptiveLevel = 1 | 2 | 3 | 4 | 5
export type AdaptiveMode = "off" | "shadow" | "automatic"

export interface AdaptiveLevelResolution {
  requested_level: AdaptiveLevel
  effective_level: AdaptiveLevel
  observed_level: AdaptiveLevel
  subject_adapter: string
  subject_adapter_version: string
  cap_reason: string | null
  eligible_for_progression: boolean
  resolution_reason: string
  state_id: string
  state_revision: number
  difficulty_policy_version: string
  progression_policy_version: string
}

export interface AdaptiveRecommendation {
  evaluation_id: string
  decision: "promote" | "demote"
  recommended_level: AdaptiveLevel
  reason_codes: string[]
  created_at: string
}

export interface AdaptiveLevelStateSummary {
  state_id: string
  student_id: string
  student_name: string
  current_level: AdaptiveLevel
  epoch: number
  revision: number
  mode: AdaptiveMode
  is_pinned: boolean
  eligible_evidence_count: number
  assessment_count: number
  overall_mastery: number | null
  assessment_mastery: number | null
  recommendation: AdaptiveRecommendation | null
  latest_resolution: AdaptiveLevelResolution | null
  difficulty_policy_version: string
  progression_policy_version: string
  detail_url: string
}

export interface AdaptiveLevelRosterResponse {
  items: AdaptiveLevelStateSummary[]
  page: number
  page_size: number
  total: number
  adaptive_mode: AdaptiveMode
  /** Whether this classroom may switch to `automatic`. The platform-wide
   *  calibration figures behind the verdict (teacher agreement, projected
   *  reversal, cohort gaps) describe every classroom on the platform, not
   *  hers, and no longer cross the wire. Mirrors
   *  apps/backend/api/schemas.py::AdaptiveLevelRosterOut. */
  automatic_available: boolean
  /** Recommendations waiting on the professor across the WHOLE classroom,
   *  not just this page — the header chip's count. */
  pending_count: number
}

/**
 * The roster row's adaptive slice (`GET /api/students/?classroom_id=`), one
 * query for the page. `pending` is the recommendation waiting on the
 * professor, if any. Mirrors apps/backend/api/schemas.py::StudentAdaptiveOut.
 */
export interface StudentAdaptiveSummary {
  level: AdaptiveLevel
  is_pinned: boolean
  pending: { direction: "up" | "down"; to_level: AdaptiveLevel } | null
}

export interface AdaptiveEvidence {
  id: string
  assignment_id: string
  /** Immutable GCS release identity; null only for deploy-window legacy evidence. */
  release_id: string | null
  source: "formal_grade" | "exercise_confirmed"
  revision: number
  raw_score: number
  captured_scale: GradeScale
  score_ratio: number
  material_type: MaterialType
  requested_level: AdaptiveLevel
  effective_level: AdaptiveLevel
  observed_level: AdaptiveLevel
  subject_adapter: string
  subject_adapter_version: string
  cap_reason: string | null
  resolution_reason: string
  eligible_for_progression: boolean
  epoch: number
  finalized_at: string
}

export interface AdaptiveLevelEvaluation {
  id: string
  trigger: string
  decision: "hold" | "promote" | "demote" | "override" | "teacher_review" | "domain_reset"
  review_outcome:
    "not_required" | "pending" | "accepted" | "rejected" | "automatic" | "manual_override"
  old_level: AdaptiveLevel
  recommended_level: AdaptiveLevel | null
  applied_level: AdaptiveLevel | null
  reason_codes: string[]
  teacher_reason: string | null
  eligible_count: number
  assessment_count: number
  overall_mastery: number | null
  assessment_mastery: number | null
  progression_policy_version: string
  education_level: EducationLevel | ""
  subject_adapter: string
  reversal_projected: boolean
  reviewed_evaluation_id: string | null
  created_at: string
}

export interface AdaptiveEngagementRisk {
  missing_count: number
  overdue_count: number
  incomplete_count: number
  pending_review_count: number
}

/**
 * The progression policy's cut-offs as 0–1 ratios, so reason copy can say
 * "al menos 8.5" on the professor's scale instead of typing a number the
 * backend owns (`copy-follows-capability.md` §2).
 * Mirrors apps/backend/api/schemas.py::AdaptiveThresholdsOut.
 */
export interface AdaptiveThresholds {
  promote_overall: number
  promote_assessment: number
  assessment_floor: number
  demote: number
  demote_latest: number
}

export interface AdaptiveLevelDetailResponse {
  state: AdaptiveLevelStateSummary
  active_evidence: AdaptiveEvidence[]
  recent_ineligible_evidence: AdaptiveEvidence[]
  latest_resolution: AdaptiveLevelResolution | null
  engagement_risk: AdaptiveEngagementRisk
  /** Optional until the backend ships them: reason copy then drops the
   *  number rather than guess it. */
  thresholds?: AdaptiveThresholds | null
  /** How many recent grades the level looks at. */
  window_size?: number | null
  history: {
    items: AdaptiveLevelEvaluation[]
    page: number
    page_size: number
    total: number
  }
}

export interface AdaptiveLevelActionResponse {
  state: AdaptiveLevelStateSummary
  evaluation: AdaptiveLevelEvaluation | null
}

export interface Classroom {
  id: string
  name: string
  // null when professor has not set a custom alias; use effective_display_name for display.
  classroom_alias: string | null
  // Mirrors Classroom.canonical_label — computed from subject/grade/section, never stored.
  canonical_label: string
  // Mirrors Classroom.effective_display_name — alias ?? canonical_label.
  effective_display_name: string
  // Mirrors Classroom.icon (PRD-30) — raw stored Lucide icon name; "" means use subject default.
  icon: string
  // Mirrors Classroom.effective_icon — icon || subject default; the value every surface renders.
  effective_icon: string
  // PRD-37 — replaces the old `subject: Subject | string` enum field with
  // three flat fields mirroring ClassroomOut. Subject names are plain text,
  // never translated (per i18n.md §11's user-generated-content exception).
  subject_id: string
  subject_name: string
  subject_default_code: string | null
  education_level: EducationLevel
  // PRD-49 — server-computed: true when the
  // subject name classifies as programming per the manifest's
  // `subject_gating` dataset (core.material_types.subject_supports_programming).
  // Decides WHERE RequestMaterialModal offers the `codigo` type — the SPA
  // never re-derives the classification (anti mirror-drift). Optional for
  // the deploy window; treat undefined as false.
  supports_programming?: boolean
  adaptive_mode: AdaptiveMode
  // PRD-48 — level recommendations waiting on the professor in this
  // classroom. Optional for the deploy window; absent reads as 0.
  adaptive_pending_count?: number
  // The school YEAR ("2025-2026"), despite the field name — NOT a semester.
  // PRD-38 relabels it "Ciclo escolar" in the UI; the rename to `school_year`
  // is deferred (ADR-0032 §Follow-ups — it feeds the engine's `semester_id`).
  semester: string
  // PRD-38 §FR-8 — the PROGRAM-WIDE period, 1..periods_per_cycle: a student is
  // in semester 1 / 3 / 9 of their WHOLE program, not of one school year. Since
  // FR-30 it also drives `canonical_label` ("Matemáticas 3°A"), in the slot
  // `grade` used to occupy. null on every pre-PRD-38 classroom ("not
  // specified"), which falls back to the old grade-based label.
  period_number: number | null
  // Legacy year level. Kept for historical rows (FR-28); new classrooms are
  // created with "" because the program-wide period already encodes it.
  grade: string
  section: string
  total_students: number
  avg_grade: number
  delivery_rate: number
  at_risk_count: number
  // PRD-45 — reversible, professor-owned declutter (not a delete). Every
  // `useClassrooms()` call with no params excludes archived rows server-
  // side, so `is_archived` is always `false` there; it's `true` only when
  // a caller explicitly opts into `{ includeArchived: true }` (the
  // /salones "Archivados" tab).
  is_archived: boolean
  archived_at: string | null
  /** PRD-61 §FR-5.3 — the evaluation cut that contains today, from the
   *  school's calendar. Null when no calendar covers the classroom or today
   *  falls between cuts. Optional for payloads cached across the deploy. */
  current_cut?: ClassroomCurrentCut | null
}

// ──────────────────────────────────────────────────────────
// Evaluation cuts (PRD-61). Mirrors the professor half of the
// wire contract (api/schemas.py — Period / Cut / CutSummary).
// ──────────────────────────────────────────────────────────

/** What the school calls one cut of its calendar. Mirrors
 *  core/models.py::EvaluationPeriod.cut_noun. */
export type CutNoun = "parcial" | "trimestre" | "bimestre"

/** Where a cut sits relative to today (Mexico City's calendar day). */
export type CutState = "closed" | "current" | "upcoming"

/** The same figures for a cut and for the whole course (PRD-61 §FR-3.2).
 *  `avg_grade` is the mean of per-student means over graded work only, on
 *  the institution's scale — never an official grade. */
export interface CutSummary {
  material_count: number
  avg_grade: number | null
  graded: number
  expected: number
  not_delivered: number
  pending_review: number
  /** Tasks from the retired gradebook (no material behind them) counted in
   *  this scope's figures. They cannot be opened, so `material_count` and
   *  `ClassroomResults.materials` never include them. Optional for the
   *  deploy window. */
  legacy_count?: number
}

/** The evaluation period a classroom falls in ("Agosto – enero"). */
export interface EvaluationPeriod {
  label: string
  start_date: string
  end_date: string
  noun: CutNoun
}

export interface EvaluationCut {
  number: number
  /** Server-built ("Parcial 2"); the SPA prefers `noun` + `number` through i18n. */
  label: string
  noun: CutNoun
  start_date: string
  end_date: string
  state: CutState
  summary: CutSummary
}

/** One sent, gradeable material of the classroom, with the cut it counts for. */
export interface ClassroomResultsMaterial {
  id: string
  title: string
  material_type: MaterialType
  /** The counted task's due date — what decides its cut. */
  due_date: string
  sent_at: string | null
  /** Null → counts only toward the whole course (outside every cut). */
  cut: number | null
  /** The professor moved it by hand (`Task.counts_for_cut`, PRD-61 §FR-2.2). */
  cut_is_manual: boolean
  avg_grade: number | null
  graded: number
  expected: number
  pending_review: number
}

/** `GET /api/classrooms/{id}/results` (PRD-61 §FR-3.1). */
/** PRD-66 — a student of the group who has no copy of a material.
 * Mirrors apps/backend/api/schemas.py::LateStudentOut. */
export interface LateStudent {
  id: string
  name: string
  has_email: boolean
}

/** PRD-66 — one of the group's materials some students do not have.
 * Mirrors apps/backend/api/schemas.py::MaterialGapOut. */
export interface MaterialGap {
  material_id: string
  title: string
  material_type: MaterialType
  status: MaterialStatus
  send_date: string | null
  student_upload_deadline: string | null
  /** Tells apart two materials with the same title (shown only then). */
  created_at: string
  /** Closed = sent and past its window: shown collapsed, never pre-selected. */
  is_open: boolean
  missing_student_ids: string[]
}

/** PRD-66 — `GET /api/classrooms/{id}/material-gaps`. */
export interface ClassroomMaterialGaps {
  students: LateStudent[]
  materials: MaterialGap[]
}

/** PRD-66 — one row of `POST /api/classrooms/{id}/material-gaps/fill`. */
export interface MaterialGapFillResult {
  material_id: string
  created: number
  error_code: string | null
}

export interface ClassroomResults {
  /** Null → the school has no calendar for this classroom; only `total` applies. */
  period: EvaluationPeriod | null
  cuts: EvaluationCut[]
  /** Number of the cut containing today, else null. */
  current_cut: number | null
  total: CutSummary
  /** Newest first. */
  materials: ClassroomResultsMaterial[]
}

export interface CutOption {
  number: number
  label: string
  noun: CutNoun
  start_date: string
  end_date: string
}

/** `GET / PATCH /api/materials/{id}/cut` (PRD-61 §FR-5.1). */
export interface MaterialCut {
  /** The cut the material counts for right now — manual or by due date. */
  cut: number | null
  is_manual: boolean
  /** The cut its due date alone would put it in, whatever the manual move
   *  says; null = outside every cut. Optional for the deploy window. */
  by_date_cut?: number | null
  options: CutOption[]
  period: EvaluationPeriod | null
}

export interface ClassroomCurrentCut {
  number: number
  label: string
  noun: CutNoun
  end_date: string
}

/** One student's average per cut (PRD-61 §FR-5.2), on the history payloads. */
export interface StudentCutAverage {
  number: number
  label: string
  noun: CutNoun
  start_date: string
  end_date: string
  avg_grade: number | null
}

/** `GET /api/home/` → `closing_cut` (PRD-61 §FR-5.4): the soonest cut that
 *  closes within seven days while work in it still waits on the professor. */
export interface HomeClosingCut {
  label: string
  noun: CutNoun
  number: number
  end_date: string
  days_left: number
  pending_review: number
  ungraded: number
  classroom_count: number
  /** Set only when exactly one classroom holds the open work — the deep link. */
  classroom_id: string | null
  classroom_name: string | null
}

// Mirrors apps/backend/core/models.py::Student.SurveyStatus (PRD-15).
export type SurveyStatus =
  "not_invited" | "skipped_no_email" | "invited" | "completed" | "bounced" | "expired"

// Mirrors apps/backend/core/models.py::OcrJob + api/schemas.py::OcrJobOut
// (PRD-19). Update both sides in the same PR.
export interface OcrJob {
  id: string
  status: "pending" | "processing" | "completed" | "failed"
  image_bytes: number
  image_mime: string
  extracted_text: string
  block_count: number | null
  avg_confidence: number | null
  error_code: string
  created_at: string
  updated_at: string
  // PRD-21 §FR-Action-endpoints — populated by ``create_ocr_job``.
  task_run_id: string | null
}

// Mirrors apps/backend/core/models.py::ReferenceDocument +
// api/schemas.py::ReferenceDocumentOut (PRD-64). Update both sides in the
// same PR.
export const REFERENCE_KINDS = ["file", "image", "text", "url"] as const
export type ReferenceKind = (typeof REFERENCE_KINDS)[number]

export const REFERENCE_STATUSES = ["pending", "processing", "ready", "failed"] as const
export type ReferenceStatus = (typeof REFERENCE_STATUSES)[number]

export interface ReferenceDocument {
  id: string
  kind: ReferenceKind
  title: string
  subject_id: string | null
  subject_name: string | null
  mime: string
  bytes: number
  source_url: string | null
  page_from: number | null
  page_to: number | null
  page_count: number | null
  char_count: number
  chunk_count: number
  language: string
  summary: string
  topics: string[]
  status: ReferenceStatus
  error_code: string
  transcribed: boolean
  used_in_materials: number
  task_run_id: string | null
  ingested_at: string | null
  created_at: string
  updated_at: string
}

export interface ReferenceListResponse {
  items: ReferenceDocument[]
}

// PRD-64 FR-18 — how an attached source is used. Mirrors
// core/reference_retrieval.py::INTENTS.
export type MaterialReferenceIntent = "basar" | "ejemplo"

export interface MaterialReferenceRef {
  document_id: string
  intent: MaterialReferenceIntent
}

// Mirrors api/schemas.py::MaterialReferenceContextOut (PRD-64 FR-32).
export interface MaterialReferenceSource {
  document_id: string
  title: string
  intent: MaterialReferenceIntent
  locators: string[]
}

export interface MaterialReferenceContext {
  round: number
  strategy: "full" | "retrieved"
  chars: number
  sources: MaterialReferenceSource[]
}

// Mirrors apps/backend/api/schemas.py::TaskRunOut (PRD-21). The shape
// is identical to the realtime ``task.progress`` payload — single
// source of truth for the wire format.
export interface TaskRun {
  task_run_id: string
  kind: "grading" | "ocr" | "material_generation" | "reference_ingest"
  status: "pending" | "running" | "completed" | "failed"
  current_step: number
  total_steps: number
  phase_code: string
  percent: number
  payload_ref_id: string | null
  payload_ref_kind: string
  error_code: string | null
  started_at: string | null
  finished_at: string | null
  metadata: Record<string, unknown>
}

// PRD-58 §FR-5 — why a student "needs attention". Mirrors
// apps/backend/core/attention.py::needs_attention: a low average over the
// last graded window, or consecutive missed deliveries. `null` when the
// student is on track (`is_at_risk === false`).
export type AttentionReason = "low_average" | "missed_deliveries"

export interface Student {
  id: string
  name: string
  email: string
  matricula: string
  persona: Record<string, unknown> | null
  classroom_id: string
  /** PRD-40 — the person this enrollment belongs to. */
  profile_id: string | null
  avg_grade: number
  is_at_risk: boolean
  attention_reason: AttentionReason | null
  /** How many tasks (`missed_deliveries`) or grades (`low_average`) the
   *  reason covers; null when not at risk. Optional for older payloads. */
  attention_n?: number | null
  delivered_count: number
  total_tasks: number
  delivery_rate: number
  // PRD-15 — survey state machine.
  survey_status: SurveyStatus
  survey_invited_at: string | null
  survey_completed_at: string | null
  /**
   * VESTIGIAL since PRD-40 Phase D — always null. The survey state belongs
   * to the person, so there is no "completed via another row" outcome left
   * to record. Kept on the wire for one release so a cached SPA still
   * parses; do not read it.
   */
  survey_completed_via_sibling_id: string | null
  // PRD-20 §FR-34 — exam tasks for this student that still need a grade.
  // Drives the roster row's "Por calificar (N)" pill + the drawer's
  // "Pendientes con foto" section. Mirrors
  // apps/backend/api/schemas.py::StudentOut.pending_exam_grading_count.
  pending_exam_grading_count: number
  /** PRD-61 — this student's figures over the scope the roster was asked for
   *  (`cut=<n>` or `cut=all`): the same counted tasks as the kardex. Absent
   *  only on a response from before the field existed. */
  cut_stats?: StudentCutStats | null
  /** PRD-48 — level + any recommendation waiting on the professor. Null when
   *  the enrollment has no adaptive state; absent on an older payload. */
  adaptive?: StudentAdaptiveSummary | null
}

/** One student's figures within one evaluation cut (PRD-61). */
export interface StudentCutStats {
  /** Null → nothing of theirs graded in this cut yet. */
  avg_grade: number | null
  graded: number
  delivered: number
  expected: number
}

export interface StudentListResponse {
  items: Student[]
  page: number
  page_size: number
  total: number
}

// Mirrors `api/schemas.py::RosterImportRowError`.
export interface RosterImportRowError {
  row: number
  // `missing_email` was missing here while the backend has emitted it since
  // PRD-04 (`api/students.py`) — the mirror drifted when that reason landed.
  reason:
    "missing_name" | "missing_email" | "invalid_email" | "duplicate_in_file" | "duplicate_existing"
  // The offending values, echoed back so the UI can name WHO failed instead of
  // only which line. Either may be "" — often that is the reason it was
  // skipped at all.
  name: string
  email: string
}

export interface RosterImportResult {
  created: number
  skipped: RosterImportRowError[]
}

// Mirrors the engine's CATALOG order. Source of truth:
// platonica-assignment-engine/src/platonica_alpha/domain/material_types.py
// (PRD-12 — dropped cuestionario + apuntes, added test + tarea;
//  PRD-49 / manifest v12 — added `codigo`, Práctica de programación).
export const MATERIAL_TYPES = ["examen", "test", "tarea", "ejercicios", "codigo"] as const
export type MaterialType = (typeof MATERIAL_TYPES)[number]

// Types offered for *new* generation. `test` is legacy-display-only; not
// offered for new generation (existing/stored `test` materials still render
// via MATERIAL_TYPES + TYPE_ICONS + the `type.test` i18n key).
// `codigo` (PRD-49) is offered CONDITIONALLY: RequestMaterialModal drops it
// unless the manifest carries the type (server-side rollout flag) AND the
// selected classroom reports `supports_programming` — no notice otherwise,
// the type simply isn't there.
export const GENERATABLE_MATERIAL_TYPES = ["examen", "tarea", "ejercicios", "codigo"] as const

// Mirrors core/models.py::Material.MaterialStatus.
//   `programado` is HISTORICAL ONLY since PRD-42 / ADR-0036 — scheduled
//   *generation* is retired, so no new material can reach it. Kept so
//   old rows still render (and still filter) instead of falling through
//   the exhaustive status maps.
export const MATERIAL_STATUSES = [
  "borrador",
  "programado",
  "procesando",
  "revision",
  // PRD-42 §FR-3 — approved, waiting for its professor-chosen send day.
  "por_enviar",
  "enviado",
  "cancelado",
] as const
export type MaterialStatus = (typeof MATERIAL_STATUSES)[number]

export type MaterialQuestionCount = number

export const MATERIAL_DISPATCH_MODES = ["immediate", "scheduled"] as const
export type MaterialDispatchMode = (typeof MATERIAL_DISPATCH_MODES)[number]

// Mirrors core.material_types.ITEM_TYPES — the question surface every
// non-coding type declares (engine manifest v8; per-type since v12).
export const MATERIAL_ITEM_TYPES = ["open_question", "multiple_choice"] as const
export type MaterialItemType = (typeof MATERIAL_ITEM_TYPES)[number]

// Mirrors core.material_types.CODING_ITEM_TYPE_CHOICES (manifest v12,
// PRD-49) — the "tipo de ejercicio" surface `codigo` declares INSTEAD of
// open_question/multiple_choice. The manifest states the full contract as
// a flat list; WHICH of these codes applies depends on the selected
// language, per the engine compatibility table mirrored in
// `@/constants/programming`.
export const CODING_ITEM_TYPES = [
  "predict_output",
  "debug_code",
  "complete_code",
  "implement_function",
  "mixed",
] as const
export type CodingItemType = (typeof CODING_ITEM_TYPES)[number]

/** Any per-type item_type code on the wire: question types OR exercise types. */
export type MaterialItemTypeCode = MaterialItemType | CodingItemType

// Mirrors core.material_types._CODING_LANGUAGE_INPUT choices (manifest
// v23). `pseudocode` is the engine's own dialect — the sentinel first entry
// and the default. `html_css` (PRD-72) is the second sentinel: markup the
// engine checks statically, never runs. Which of these a professor may pick,
// and with which exercise types, lives in `@/constants/programming`.
export const PROGRAMMING_LANGUAGES = [
  "pseudocode",
  "python",
  "java",
  "cpp",
  "javascript",
  "c",
  "html_css",
] as const
export type ProgrammingLanguage = (typeof PROGRAMMING_LANGUAGES)[number]

// ── PRD-65 — the editable content of one student's release ──────────
// Mirrors apps/backend/api/material_assignments.py (content, content-edits,
// compile). `MaterialQuestion` used to live here: an open `[key: string]:
// unknown` shape nothing imported. This is the typed replacement.

export interface ContentHeader {
  school_name: string
  school_city: string
  has_logo: boolean
  professor_name: string
  title: string
  subtitle: string | null
  metadata_rows: { label: string; value: string }[]
  instructions: string[]
  language: "es" | "en"
}

export interface ContentOption {
  label: string
  text: string
}

export interface ContentItem {
  index: number
  item_type: "multiple_choice" | "open_question"
  stem: string
  options: ContentOption[]
  answer: string
  answer_support: { explanation?: string; memory_tip?: string } | null
  // False when the engine verifies the answer by computation: the stem is
  // part of the proof, so the professor cannot reword it.
  editable: boolean
  not_editable_reason: "deterministic_open_math" | null
  has_math: boolean
}

export interface AssignmentContent {
  header: ContentHeader
  items: ContentItem[]
  validation_warnings: string[]
}

/** One edited item, as sent to `PUT /content-edits` and stored in the draft. */
export interface ContentEditItem {
  index: number
  stem: string
  options: ContentOption[]
}

export interface ContentEdits {
  version: 1
  items: ContentEditItem[]
  edited_at: string
  // Set by a failed compile; cleared on the next save.
  compile_error?: { error_code: string; error_context: Record<string, unknown> | null }
}

/**
 * none      — the PDFs match the content.
 * dirty     — a saved draft the PDFs do not include yet.
 * compiling — a TaskRun is rendering the draft.
 * failed    — the last compile failed; the draft is still there.
 */
export type AssignmentEditState = "none" | "dirty" | "compiling" | "failed"

export interface AssignmentContentResponse {
  content: AssignmentContent
  content_edits: ContentEdits | null
  edit_state: AssignmentEditState
  edit_error_code: string | null
  compile_task_run_id: string | null
}

// Mirrors the `request_payload` JSON written by api/materials.py::
// generate_material. PRD-42 dropped `send_date` + `dispatch_mode` from
// it: generation is always immediate and the send day is chosen at
// approval, so neither belongs to the *request*.
export interface MaterialRequestPayload {
  classroom_id: string
  material_type: MaterialType
  topic: string
  question_count: MaterialQuestionCount
  notes: string
  // Question codes for the four question types; exercise codes for `codigo`.
  item_type: MaterialItemTypeCode
  // PRD-49 — present only when material_type is `codigo` (the backend
  // writes the key only for types whose contract declares the input).
  language?: ProgrammingLanguage
  topic_subject_override: boolean
  education_level: EducationLevel
  // PRD-64 — the sources attached to the request, so a duplicate prefills them.
  references?: MaterialReferenceRef[]
}

export interface GradingCompletion {
  graded: number
  total: number
  task_id: string | null
  // PRD-58 — mean of the graded records for this material, in the
  // professor's grade scale. Null until at least one record is graded.
  avg_grade: number | null
}

export interface Material {
  id: string
  title: string
  topic: string
  material_type: MaterialType
  education_level: EducationLevel
  status: MaterialStatus
  /** PRD-69 — `staff` when Platonica prepared and sent it. */
  origin: MaterialOrigin
  dispatch_mode: MaterialDispatchMode
  created_at: string
  updated_at: string
  send_date: string | null
  scheduled_for: string | null
  // PRD-25 §FR-10 — date by which students must upload their work.
  // Captured on the "Aprobar y enviar" step; null for rows that pre-
  // date PRD-25's picker (back-compat). Threads through to
  // Task.due_date so the gradebook drawer + roster pills render the
  // right "vence" date.
  student_upload_deadline: string | null
  sent_at: string | null
  cancelled_at: string | null
  request_payload: MaterialRequestPayload | null
  /**
   * What the ENGINE did with the requested question format. Manifest v8
   * applies the choice to every subject; `applies: false` remains readable
   * for historical callbacks and any future scoped contract. Null on
   * materials generated before the engine reported it.
   * Mirrors `api/schemas.py::MaterialOut.item_type_resolution`.
   */
  item_type_resolution: {
    requested: string
    effective: string
    applies: boolean
    reason: string
  } | null
  notes: string
  assigned_to: string
  delivery_status: string | null
  classroom_id: string
  classroom_name: string
  // PRD-11 — replaces gcp_job_id (Celery task) with the Cloud Run Jobs
  // execution name produced by executions.create.
  engine_execution_name: string
  grading_completion: GradingCompletion
  // PRD-41 §FR-5.2 — how many (student, task) cells of this material are
  // waiting on the professor. Drives the "Revisar entregas (N)" CTA;
  // 0 means the card shows nothing.
  pending_review_count: number
  // PRD-11 — per-student aggregate counts. The progress strip + per-student
  // grid live-update against /api/materials/{id}/progress; these embedded
  // counts cover lighter surfaces (cards, list).
  assignments_total: number
  assignments_pending: number
  assignments_processing: number
  assignments_ready: number
  assignments_failed: number
  /** PRD-59 — the engine code most of this material's failed assignments
   *  share, so a list row can tell an input rejection (re-running the stored
   *  request cannot help) from a transient one. Bucket it through
   *  `constants/materialErrors.ts::materialFailureKind`. Empty when nothing
   *  failed. Mirrors apps/backend/api/schemas.py::MaterialOut. */
  dominant_error_code: string
  // From the engine's material-types contract; controls whether the SPA
  // shows "Ver respuestas" buttons.
  produces_professor_pdf: boolean
  // PRD-29 — false for practice-only types (`ejercicios`). Drives the
  // practice card variant + hides the grading strip / upload-deadline
  // picker. Mirrors core.material_types.is_gradeable_material_type; the
  // SPA reads this boolean rather than re-deriving the rule in TS.
  creates_grade_record: boolean
  // PRD-44 follow-up — the same six-bucket tally the review queue shows
  // (`ReviewQueueSummary`, defined below), surfaced on the material
  // detail page. Opt-in on the backend (`to_schema`'s
  // `include_review_summary`): null on every list row, populated only
  // by `GET /api/materials/{id}`.
  review_summary: ReviewQueueSummary | null
  // PRD-62 — set when the professor archived this (sent) material: it stops
  // counting in every grade and its portal link stops working. The status is
  // untouched, so an archived material is still `enviado`. List endpoints hide
  // archived rows unless asked with `?archived=true`.
  // Mirrors apps/backend/api/schemas.py::MaterialOut.archived_at.
  archived_at: string | null
  // PRD-63 — the current version (1 = first generation) and how many more
  // regenerations the server allows. Both on every row.
  round: number
  regenerations_left: number
  // Detail only (`GET /api/materials/{id}`): the regenerations so far, and
  // how many enrolled students have no copy yet. Empty / 0 on list rows.
  rounds: MaterialRound[]
  // PRD-64 — detail only: what the current round was generated from.
  reference_context?: MaterialReferenceContext | null
  roster_gap: number
  // PRD-66 — detail only: WHO the gap is (capped server-side; `roster_gap`
  // stays the exact count). Mirrors api/schemas.py::MaterialOut.
  roster_gap_students?: LateStudent[]
  // PRD-66 — every row: missing students while the material is still OPEN
  // for them (`core/late_students.py::is_open`), 0 once its window closed.
  // The `/materiales` badge reads this; `roster_gap` counts regardless.
  open_roster_gap: number
  // PRD-66 — every row: whether generating for missing students applies at
  // all for this status (`core/late_students.py::is_eligible`). The SPA
  // reads it instead of mirroring the server's status gate.
  accepts_late_students: boolean
  // PRD-65 — students whose edited content the PDFs do not include yet, and
  // students whose edited PDFs are rendering. Approve refuses while either
  // is above zero (409 `material_edits_uncompiled`).
  dirty_count: number
  compiling_count: number
}

// PRD-63 — mirrors apps/backend/api/schemas.py::MaterialRoundOut.
export interface MaterialRound {
  number: number
  created_at: string
  reasons: RegenerationReason[]
  // The professor's own words, shown back only to them.
  comment: string
}

// Mirrors the keys of apps/backend/core/constants.py::
// MaterialRegenerationConfig.reason_sentences.
export type RegenerationReason =
  | "more_practice"
  | "clearer_examples"
  | "clearer_instructions"
  | "closer_to_topic"
  | "less_repetitive"

// Engine material-types contract entry — mirrors the backend mirror of
// the engine's CATALOG. Fetched via GET /api/material-types.
//
// Source of truth (engine):
//   platonica-assignment-engine/src/platonica_alpha/domain/material_types.py
// Mirror (web backend):
//   platonica-web/apps/backend/core/material_types.py
// Reference document:
//   platonica-assignment-engine/docs/contracts/material-types.md

// Manifest v11 — one selectable programming language on a `codigo`
// request. Labels ship in both locales from the backend mirror; the SPA
// picks by active locale (they are contract data, not UI copy).
export interface LanguageChoiceContract {
  code: ProgrammingLanguage | string
  label_es: string
  label_en: string
}

// Mirrors core.material_types.LanguageInputContract — declared only by
// coding material types; `null` for every question type.
export interface LanguageInputContract {
  field: "language"
  choices: LanguageChoiceContract[]
  default: ProgrammingLanguage | string
  required_in_production: boolean
}

export interface MaterialInputContract {
  requires_topic: boolean
  requires_question_count: boolean
  accepts_extra_instructions: boolean
  adaptive_level: {
    field: "adaptive_level_snapshot"
    level_field: "level"
    choices: AdaptiveLevel[]
    default: 1
    policy_version: "adaptive-difficulty-v1"
    server_resolved: true
    student_editable: false
    required_in_production: false
  } | null
  // Per-type choices since manifest v12: question codes for the four
  // question types, exercise codes for `codigo`.
  item_type: {
    choices: MaterialItemTypeCode[]
    subject_scope: "all_subjects"
    required_in_production: boolean
    default: MaterialItemTypeCode
  } | null
  // Manifest v21 — absent on assessment types; shared-document flow only.
  document?:
    | {
        generation_scope: "classroom_shared"
        depth_field: "depth"
        depth_choices: ("essential" | "standard" | "extended")[]
        default_depth: "standard"
        schema_version: 1
        editable_sections: boolean
        book_export: boolean
        max_topics: number
        max_section_chars: number
        max_document_bytes: number
        max_book_chapters: number
      }
    | {
        // Manifest v22 — teacher-private plan_clase; distinct from shared notes.
        generation_scope: "teacher_private"
        schema_version: 1
        editable_sections: boolean
        book_export: false
        default_duration_minutes: number
        min_duration_minutes: number
        max_duration_minutes: number
        default_include_history: boolean
        max_topics: number
        max_section_chars: number
        max_document_bytes: number
        max_syllabus_chars: number
        generated_faq_min: number
        generated_faq_max: number
        edited_faq_min: number
        edited_faq_max: number
        section_keys: string[]
      }
  topic_subject: {
    override_field: "topic_subject_override"
    subject_scope: "non_math_non_physics"
    default_override: boolean
    enforcement: "registry_proven_only"
  }
  shared_questions: {
    enabled_field: "shared_questions_enabled"
    fraction_field: "shared_question_fraction"
    subject_scope: "non_physics"
    default_enabled: boolean
    default_fraction_when_enabled: number
    fraction_min_exclusive: number
    fraction_max_inclusive: number
    count_strategy: "floor_min_one"
  } | null
  // Manifest v11 (PRD-49) — non-null only for `codigo`.
  language: LanguageInputContract | null
  explicit_count_choices: number[]
}

export interface MaterialOutputContract {
  student_pdf: boolean
  professor_pdf: boolean
  student_responses_json: boolean
  manifest_json: boolean
  markdown_debug: boolean
  // Manifest v13 (PRD-50) — true only for `codigo`, whose release carries a
  // private grading_bundle.json sidecar. Mirrors
  // apps/backend/core/material_types.py::MaterialOutput; the SPA does not
  // branch on it today, it is here so the type matches the wire.
  grading_bundle_json: boolean
  // Manifest v16 (PRD-52) — true only for `codigo`, whose release carries a
  // sixth object, the student's editable starter file. Mirrors
  // apps/backend/core/material_types.py::MaterialOutput; the SPA does not
  // branch on it (the per-assignment `starter_code_expected` carries that
  // decision already), it is here so the type matches the wire.
  starter_code_file: boolean
  content_json?: boolean
  // Manifest v22: present only on plan_clase; never a student or answer-key PDF.
  teacher_document_pdf?: boolean
}

export interface MaterialTypeContract {
  code: string
  label_es: string
  label_en: string
  aliases: string[]
  inputs: MaterialInputContract
  outputs: MaterialOutputContract
  allowed_item_counts: number[]
  default_items: number | null
  min_items: number | null
  max_items: number | null
  description_es: string
  description_en: string
  // Manifest v11 — `not programming_policy.practice_only` on the engine;
  // derived from _NON_GRADEABLE_CODES on the web mirror. True for
  // examen/test/tarea/codigo, false for ejercicios.
  gradeable: boolean
}

// PRD-12 — persona schema. The SPA reads this from /api/material-types
// and renders the persona-edit modal data-driven from it.
export interface PersonaFieldContract {
  key: string
  label_es: string
  label_en: string
  kind: "text" | "long_text" | "list"
  required: boolean
  tier: "web_canonical" | "engine_native"
  purpose: string
}

/**
 * Manifest v11 top-level `subject_gating` — the UI-facing, versioned
 * dataset that classifies subject names as programming/informatics.
 * The SPA does NOT evaluate it: the backend applies it via
 * `subject_supports_programming` and ships the result as
 * `Classroom.supports_programming`. Mirrored here only so the wire shape
 * is fully typed.
 */
export interface SubjectGatingContract {
  version: number
  normalization: string
  match: string
  groups: Record<string, string[]>
}

export interface MaterialTypesResponse {
  version: number
  subject_gating: SubjectGatingContract
  types: MaterialTypeContract[]
  personalization: PersonaFieldContract[]
  education_levels: {
    field: "education_level"
    choices: EducationLevel[]
    default: "highschool"
    required_in_production: false
  }
}

// PRD-11 — one personalized PDF artifact per (Material, Student).
export type MaterialAssignmentStatus = "pending" | "processing" | "ready" | "failed"

// PRD-13 §FR-1.1 — per-assignment email delivery state.
// Mirrors apps/backend/core/models.py::MaterialAssignment.EmailStatus.
export type EmailStatus =
  "not_sent" | "skipped_no_email" | "queued" | "sent" | "bounced" | "complained" | "failed"

export interface MaterialAssignment {
  id: string
  material_id: string
  // PRD-29 — material type + title denormalized so the Student-detail
  // "Materiales" section can group practice (`ejercicios`) deliveries
  // under a separate subsection and label rows with a real title.
  material_type: MaterialType
  material_title: string
  student_id: string
  student_name: string
  task_index: number
  status: MaterialAssignmentStatus
  student_pdf_available: boolean
  professor_pdf_available: boolean
  // PRD-52 §FR-9 — the student's editable starter file. Two facts, both
  // derived on the backend (api/material_assignments.py::to_schema) so the
  // SPA never mirrors "codigo is the type that has one":
  //   expected  — the material type's contract declares `starter_code_file`.
  //               False ⇒ render no affordance at all.
  //   available — this release actually carries one. False on a `codigo`
  //               material generated before manifest v16, or still
  //               processing ⇒ render the affordance disabled.
  starter_code_expected: boolean
  starter_code_available: boolean
  error_code: string
  error_detail: string
  error_context: TopicSubjectErrorContext | null
  attempts: number
  started_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  // PRD-13 — email delivery state.
  email_status: EmailStatus
  email_to: string
  email_message_id: string
  email_attempts: number
  email_queued_at: string | null
  email_sent_at: string | null
  email_bounced_at: string | null
  email_failed_at: string | null
  email_error_code: string
  // PRD-24 follow-up — id of the latest ProgressUpload tied to this
  // (student, task). Surfaced on the materials detail per-student card
  // as a "Ver respuestas" button. Null when no upload exists.
  latest_upload_id: string | null
  // PRD-58 — that upload's lifecycle status (ProgressUploadStatus) so the
  // results row can say "Por revisar" and deep-link into the queue.
  latest_upload_status: string | null
  // PRD-68 §FR-8.2 — who put that upload there and, for a portal
  // submission, which version it is. Optional for the deploy window.
  latest_upload_source?: ProgressUploadSource | null
  latest_upload_version?: number | null
  // PRD-48 — the level this student's PDF was generated at (the
  // assignment's immutable snapshot). Null for a material generated before
  // adaptive levels, or with no snapshot. Optional for the deploy window.
  adaptive_level?: AdaptiveLevel | null
  // PRD-58 — the student's formal result for this material, read off the
  // ProgressRecord of (student, material's Task). `grade` is in the
  // professor's scale; all three are null/false when the material creates
  // no grade record (PRD-29) or the record hasn't been seeded yet.
  grade: number | null
  delivered: boolean
  record_status: ProgressStatus | null
  // PRD-65 — whether this release carries editable content (false before
  // manifest v20), the draft's state, the last compile error and the
  // TaskRun rendering it. Mirrors api/material_assignments.py::to_schema.
  content_available: boolean
  edit_state: AssignmentEditState
  edit_error_code: string | null
  compile_task_run_id: string | null
  // PRD-73 §FR-5 — «Dar más tiempo». Computed by the backend in the same
  // batched queries as the rest of the row (`core/portal_reopen.py` owns
  // eligibility; the SPA never re-derives it). Optional for the deploy
  // window: an older backend omits them, and a missing field reads as "not
  // eligible, never reopened", which leaves the row exactly as it was.
  /** The last Mexico City day (`YYYY-MM-DD`) of the active or past reopen. */
  submission_reopened_through?: string | null
  reopen_eligible?: boolean
  reopen_block_reason?: ReopenBlockReason | null
  /** The day a reopen NOW would grant; null when not eligible. */
  reopen_preview_through?: string | null
  /**
   * What the reopen email would do now, decided by the server with the same
   * function that sends it (core/portal_reopen.py::email_preview). The
   * confirmation reads this instead of guessing from `email_to`, which is
   * the last delivery's address and blank when that send was skipped.
   */
  reopen_email_preview?: ReopenEmailPreview | null
  /** The latest upload arrived after the ORIGINAL deadline (`is_late`). */
  latest_upload_late?: boolean | null
}

// PRD-73 §FR-5 — the reopen email's outcome as predicted before the tap.
// Mirrors core/portal_reopen.py::EmailOutcome minus `failed`, which only a
// send can produce.
export type ReopenEmailPreview = "sent" | "no_email" | "suppressed" | "blocked"

// PRD-73 §FR-2 — why a row cannot be reopened, in the order the backend
// checks. Mirrors core/portal_reopen.py::reopen_eligibility.
export type ReopenBlockReason =
  "material_archived" | "reopen_not_supported" | "reopen_before_deadline" | "reopen_scanned"

// PRD-73 §FR-3.3 — what happened to the reopen email. Mirrors
// api/schemas.py::ReopenOut.email_outcome.
export type ReopenEmailOutcome = "sent" | "no_email" | "suppressed" | "blocked" | "failed"

/**
 * PRD-73 §FR-3.3 — `POST /api/material-assignments/{id}/reopen`.
 * Mirrors api/schemas.py::ReopenOut.
 *
 * `portal_url` is the plaintext link, returned only on the response that
 * minted it (null on `already_open`, a duplicate request inside the 10 s dedupe window). It is
 * never stored, logged or sent to analytics by the SPA either.
 */
export interface ReopenOut {
  assignment: MaterialAssignment
  /** `YYYY-MM-DD`, the last Mexico City day the window stays open. */
  reopened_through: string
  /** The instant it closes: 00:00 of the day after, Mexico City. */
  closes_at: string
  grade_cleared: boolean
  previous_grade: number | null
  email_outcome: ReopenEmailOutcome
  portal_url: string | null
  already_open: boolean
}

/** PRD-73 §FR-4.4 — `POST /api/materials/{id}/reopen`. Mirrors api/schemas.py::BulkReopenOut. */
export interface BulkReopenOut {
  reopened_through: string
  reopened: number
  grades_cleared: number
  emailed: number
  without_email: number
  skipped: { assignment_id: string; reason: string }[]
}

/**
 * PRD-52 §FR-9 — one `codigo` assignment's starter file.
 * Mirrors apps/backend/api/schemas.py::StarterCodeOut.
 *
 * `filename` is the student-facing name read off the object key's last
 * segment (`<prefix>/starter/<basename>`), verbatim: Java couples the
 * filename to the public class, so `Solution.java` cannot be renamed or
 * slugged. `content` is capped server-side; `truncated` says so, and the
 * download route always serves every byte.
 */
export interface StarterCode {
  filename: string
  content: string
  byte_size: number
  truncated: boolean
}

export interface TopicSubjectErrorContext {
  topic: string
  subject: string
  classification: string
  confidence: "registry_proven" | "marker_suspected" | null
  expected_domain: string | null
  detected_domain: string | null
}

export interface MaterialAssignmentListResponse {
  items: MaterialAssignment[]
  page: number
  page_size: number
  total: number
}

export interface MaterialProgress {
  material_id: string
  status: MaterialStatus
  totals: {
    total: number
    pending: number
    processing: number
    ready: number
    failed: number
  }
  started_at: string | null
  first_completed_at: string | null
  last_completed_at: string | null
  eta_seconds: number | null
}

export interface MaterialListResponse {
  items: Material[]
  page: number
  page_size: number
  total: number
}

// Backend Task (apps/backend/core/models.py::Task) and ProgressRecord mirrors.
export interface Task {
  id: string
  name: string
  due_date: string
  material_id: string | null
  // PRD-20 §FR-35 — exam check is in-band so the matrix renders the
  // 📷 affordance only on exam columns. `null` for ad-hoc tasks.
  material_type: MaterialType | null
  classroom_id: string
}

// PRD-08: explicit lifecycle. `null` when no record exists for the pair.
export type ProgressStatus = "awaiting" | "pending_grading" | "graded" | "not_delivered"

// PRD-20 §FR-36 — in-flight grading state attached to a matrix cell.
// `phase` mirrors ocr_metadata.phase; status carries the ProgressUpload
// lifecycle bucket. v1.4 §FR-56 added `pending_review` as a
// quasi-terminal state distinct from `processing` — the worker
// finished, score is computed, but the strict confidence gate didn't
// pass so the professor must confirm.
// PRD-68 — `superseded`: a portal version the student replaced. Never the
// cell; kept for history only.
export type PendingUploadStatus =
  "pending_grading" | "processing" | "pending_review" | "graded" | "failed" | "superseded"

export interface PendingUpload {
  id: string
  status: PendingUploadStatus
  phase: string | null
  draft_score: number | null
  auto_accept_eligible: boolean
  // v1.4 §FR-61: review_summary.needs_review_count flattened onto the
  // cell-level shape so the matrix/drawer/student-page can render the
  // "N por revisar" badge without fetching the full ocr_metadata.
  // Optional — older rows that haven't been re-graded since v1.4 are
  // null; treat null and 0 the same.
  needs_review_count?: number | null
}

export interface ProgressCell {
  delivered: boolean | null
  grade: number | null
  notes: string
  has_upload: boolean
  status: ProgressStatus | null
  // PRD-20 §FR-36 — null when no upload exists OR when the cell is
  // already graded (saved grade takes precedence). Drives the new
  // `procesando` / `revisar` pills.
  pending_upload: PendingUpload | null
  // PRD-06 §FR-21 follow-up + PRD-20 §FR-66 — id of the ProgressUpload
  // attached to this cell, surfaced on BOTH pending and graded cells
  // so the matrix's click-to-preview affordance can open the existing
  // <UploadPreviewLightbox>. null for manual-entry grades + empty
  // cells (the explicit "skip if grade was added manually" cut).
  upload_id: string | null
}

export interface StudentProgressRow {
  id: string
  name: string
  email: string
  matricula: string
  persona: Record<string, unknown> | null
  avg_grade: number
  is_at_risk: boolean
  delivered_count: number
  graded_count: number
  pending_grading_count: number
  awaiting_count: number
  total_tasks: number
  records: Record<string, ProgressCell> // keyed by task id
}

export interface ProgressSheetResponse {
  items: StudentProgressRow[]
  tasks: Task[]
  page: number
  page_size: number
  total: number
}

export interface ProgressStats {
  avg_grade: number
  delivery_rate: number
  at_risk_count: number
  pending_grading_count: number
  awaiting_count: number
  total_students: number
  total_tasks: number
}

// Mirrors apps/backend/core/models.py::ProgressUpload +
// api/schemas.py::ProgressUploadOut.
// PRD-20 v1.4 §FR-56 — adds `pending_review` for uploads whose strict
// confidence gate didn't pass. Mirrors `core.models.ProgressUpload.UploadStatus`.
// PRD-68 adds `superseded` (a portal version the student replaced).
export type ProgressUploadStatus =
  "pending_grading" | "processing" | "pending_review" | "graded" | "failed" | "superseded"

/** Mirrors `core.models.ProgressUpload.Source` (PRD-68). */
export type ProgressUploadSource = "professor" | "student_portal"

// PRD-20 §FR-15 — populated by the photo-grading task once OCR +
// matching land. `null` during PENDING_GRADING / PROCESSING. The
// `final` sub-key is set on professor save (PRD-20 §FR-20) so a
// later edit doesn't lose the original auto-grade trail.
// PRD-20 v1.4 §FR-60: per-question `needs_review` flag — true when the
// matcher couldn't confidently grade this question (low conf, no_match
// with non-blank source, probable_match). Routes the question through
// the amber "Revisar" badge in the ReviewPanel.
// PRD-43 §FR-4.1 — the VLM judge writes four more keys per question.
// ALL FOUR ARE OPTIONAL: rows graded before the judge shipped (and rows
// from the lexical fallback path) carry none of them and must render
// exactly as they did before.
//   `grader`             — "vlm_judge" when the judge produced this row.
//   `verdict`            — refines `outcome`, never replaces it (§FR-2.5).
//   `feedback`           — ONE sentence for the professor. Never collapse
//                          it: the contract is one sentence, so a clamp
//                          would hide the half that matters.
//   `uncertainty_reason` — why the judge doubted. Empty string when it
//                          didn't, so treat "" as absent.
// ── PRD-50 — deterministic code evaluation (material_type `codigo`) ──
// Mirrors apps/backend/core/code_grading.py::_question_row. One verdict
// exercise maps onto the existing per-question shape: `expected` /
// `detected` stay "" (the reference solution is bundle-private; the
// student's code renders from `GradingMetadata.code_submission`), and the
// code-specific breakdown travels in the `code` sub-dict below.

// Wire statuses from the engine's verdict (PRD-50 §FR-7).
export type CodeExerciseStatus = "passed" | "failed" | "parse_error" | "not_found"

/** The first public test case the student's code failed: f(args) → expected vs got. */
export interface CodeCaseFailure {
  args: unknown[]
  expected: unknown
  got: unknown
}

/**
 * PRD-72 — an `html_css` exercise has no cases to run: its first failed
 * public check is one printed requirement plus what the checker found
 * instead ("Encontramos 0 elementos h1").
 */
export interface MarkupRequirementFailure {
  requirement: string
  detail: string
}

export interface GradingQuestionCode {
  exercise_id: string
  status: CodeExerciseStatus
  /**
   * PRD-50 — which KIND of exercise this row is. Per-exercise (a `mixed`
   * practice has a different type per exercise), falling back server-side
   * to the material's own `item_type`. It is what stops the review screen
   * from describing a `predict_output` submission — where the student
   * hands in an OUTPUT, not code — as a function that could not be found.
   * `null` on rows written before the field existed.
   */
  exercise_type?: CodingItemType | string | null
  /**
   * PRD-72 — the language the submission was evaluated in, copied onto
   * every exercise so a row can say "sección" instead of "función". `null`
   * on rows written before the field existed (all code, never markup).
   */
  language?: ProgrammingLanguage | string | null
  public_passed: number | null
  public_total: number | null
  hidden_passed: number | null
  hidden_total: number | null
  first_failed_public: CodeCaseFailure | MarkupRequirementFailure | null
  constructs_ok: boolean | null
  /**
   * null on `parse_error` / `not_found` (§9.6 — ⚠ / "no encontrada",
   * never an automatic 0 the professor didn't choose).
   */
  score_fraction: number | null
}

export interface GradingQuestion {
  index: number
  expected: string
  detected: string
  outcome: GradingOutcome
  confidence: number
  needs_review?: boolean
  grader?: string
  verdict?: GradingVerdict | string
  feedback?: string
  uncertainty_reason?: string
  /**
   * PRD-67 — the reader's verbatim quote of where on the page this answer
   * is, and whether that quote is actually in its own transcript. Absent on
   * rows written before PRD-67; `null` when no quote was reported.
   */
  evidence?: string | null
  evidence_missing?: boolean
  /**
   * PRD-67 §FR-3.6 — the reader reported this blank without having found the
   * question or read every page; it arrives as `unreadable`, never "no
   * contestó". Absent on rows written before that change.
   */
  not_found?: boolean
  /**
   * PRD-50 — present only on `codigo` uploads evaluated by the engine
   * (`grader === "code_eval"`). Question rows from the photo/judge
   * pipelines never carry it; render exactly as before when absent.
   */
  code?: GradingQuestionCode
}

// PRD-20 v1.4 §FR-58: extended accept_path values. `manual_review_required`
// is written automatically by the Celery task when the strict gate
// didn't pass.
//
// Declared once in `@/constants/grading` (which is what `@api/grading`
// already imports) and re-exported here so both spellings resolve to the
// same union.
export type { GradingAcceptPath }

export interface GradingFinalSnapshot {
  final_score: number
  accept_path: GradingAcceptPath
  saved_at: string
  questions: GradingQuestion[]
}

// PRD-20 v1.4 §FR-65 — per-image OCR audit trail. Drives the
// "Ver lo que leímos" disclosure + the rotation badges in the
// ReviewPanel. ``source`` is ``"vision"`` for image uploads,
// ``"pdf_text_layer"`` for PDF uploads (engine output or local fixture).
// PRD-43 §FR-1.4: `source` gains `"vlm_judge"` — the judge returns one
// `page_transcripts[]` entry per image, which is what keeps "Ver lo que
// leímos" alive now that the Vision loop is gone. Those rows carry no
// per-block Vision statistics, so `avg_confidence` / `rotated` /
// `block_count` are all null on them. Every consumer must already tolerate
// a null `avg_confidence` (the pypdf path has written one since v1.4).
export interface GradingImageRecord {
  seq: number
  raw_text: string
  avg_confidence: number | null
  rotated: boolean | null
  block_count: number | null
  source: "vision" | "pdf_text_layer" | "vlm_judge" | string
}

// PRD-20 v1.4 §FR-61 — top-level aggregate the ReviewPanel banner reads.
//
// EVERYTHING BUT `needs_review_count` IS OPTIONAL, and that is not defensive
// styling — it is what the backend writes. The degrade path
// (`core/tasks.py::_finalize_pending_review`) writes the single key
// `{"needs_review_count": 0}`; the scored path writes the rest. Declaring the
// scored shape as required made every read of the other keys a lie the
// compiler endorsed.
export interface GradingReviewSummary {
  needs_review_count: number
  low_clarity_indexes?: number[]
  // Absent since PRD-43: rotation was a Vision block-geometry heuristic
  // and the judge is rotation-robust, so the backend stopped writing it
  // rather than pinning a misleading 0. Legacy rows still carry it.
  rotated_image_count?: number
  /**
   * PRD-43 §FR-2.4 renames this to `readable_ratio` (verdicts that are
   * neither `blank` nor `unreadable`, over total). `extraction_yield` stays
   * aliased to the same value for one release, so read `readable_ratio`
   * first and fall back.
   */
  extraction_yield?: number
  readable_ratio?: number
  /**
   * PRD-43 telemetry counters — how many questions the judge pulled back from
   * a lexical `incorrect` (`rescued_count`) and how many it marked wrong that
   * the lexical matcher had passed (`stricter_count`). Written by
   * `core/tasks.py` on the scored path only; absent on legacy + degraded rows.
   */
  rescued_count?: number
  stricter_count?: number
}

// `ocr_metadata` is a JSONB passthrough with NO response schema — the backend
// writes whichever dict the branch it took builds, and nothing fills the gaps
// on the way out. Two shapes exist in the wild:
//
//   scored   — `core/tasks.py` ~:2196, every key below.
//   degraded — `core/tasks.py::_finalize_pending_review`, ONLY
//              `{phase, reason, actor, questions, images, review_summary}`.
//              Judge unreachable or no answer key (PRD-43 §FR-6.1 / §FR-5.3).
//
// Degraded rows already exist in staging and will never be backfilled, so the
// scoring keys are optional here on purpose. `undefined === null` is false —
// typing them `T | null` is what let `x === null ? … : x.toFixed(1)` blank the
// whole review screen. Read them through `@lib/gradingMetadata`, which folds
// absent / null / NaN into one `null`.
// PRD-67 — the blind reader's call on "are these images this material's
// answer sheet?". Mirrors `core/vlm_judge.py::SubmissionCheck.as_dict`.
export interface GradingSubmissionCheck {
  is_answer_sheet: "yes" | "no" | "unsure" | string
  matched_question_indexes: number[]
  /** One sentence the reader wrote for the professor, in Spanish. */
  reason: string
}

/**
 * PRD-67 §FR-3.6 — pages the reader transcribed against pages it received,
 * and the answers it reported blank without having found them (shown as
 * unreadable). Mirrors `core/vlm_judge.py::ReadingCoverage.as_dict`.
 */
export interface GradingReadingCoverage {
  pages_expected: number | null
  pages_read: number
  not_found_indexes: number[]
  rereads: number
}

export interface GradingMetadata {
  phase: "scored" | "awaiting_review" | string
  /**
   * Why the pipeline routed this upload to manual review. Written only on the
   * degrade path and mirrors `ProgressUpload.error_code` — prefer the column,
   * which the queue row carries too.
   */
  reason?: string
  answer_key_source?: string | null
  answer_key_cached_at?: string | null
  questions?: GradingQuestion[]
  /**
   * Absent on rows the pipeline never scored (judge unavailable, no answer
   * key). The professor still grades those by hand from the photos, so the
   * review surfaces render a dash rather than crashing on `.toFixed`.
   */
  draft_score?: number | null
  auto_accept_eligible?: boolean
  questions_total?: number | null
  questions_correct?: number | null
  /**
   * PRD-43 §FR-2.3 — the name / email the judge read off the sheet header did
   * not match the student this grade is about to be written to. Written by
   * `core/tasks.py` ~:2210 specifically so the review screen can warn instead
   * of silently grading a sheet that may belong to someone else.
   */
  identity_mismatch?: boolean
  /**
   * The evidence behind `identity_mismatch`. The backend only raises the
   * flag when the address it read resolves to another student in the SAME
   * classroom, so `identity_suspected_student` is a roster name, not a
   * guess. Stored because a verdict without its evidence can't be
   * explained from the row — which is exactly how a false positive went
   * undiagnosable on 2026-08-07.
   * Mirrors `core/tasks.py` (`_finalize_pending_review` + the scored path).
   */
  identity_detected_email?: string
  identity_suspected_student?: string
  /**
   * Which lane produced the upload: `"professor"` or `"student:via_portal"`
   * (PRD-24 §FR-5.3). Preserved across both the scored and the degrade path so
   * audit + observability can tell the lanes apart.
   */
  actor?: string
  // v1.4 additions — optional so older `ocr_metadata` rows still parse.
  images?: GradingImageRecord[]
  review_summary?: GradingReviewSummary
  final?: GradingFinalSnapshot
  /**
   * Key entries the pipeline could not resolve to a question, by index.
   * Written by `core/tasks.py` since PRD-20 and read nowhere until
   * PRD-43 §FR-4.4 required it in the reasons list.
   */
  unresolved_key_indexes?: number[]
  /**
   * PRD-67 — the reader's call on the images (`null` when no reader ran) and
   * the flags its output could not vouch for. Absent on rows written before
   * PRD-67; read the flags through `@lib/gradingMetadata::gradingReviewFlags`.
   */
  submission_check?: GradingSubmissionCheck | null
  review_flags?: string[]
  /** Absent on rows written before PRD-67 §FR-3.6; `null` when no reader ran. */
  reading_coverage?: GradingReadingCoverage | null
  /**
   * PRD-50 — the student's code, exactly as extracted from their text /
   * docx files (concatenated in upload order, §9.7). Written by
   * `core/tasks.py::_grade_codigo_submission` BEFORE eval dispatch, so it
   * survives every later outcome (verdict, zombie sweep, regrade). Its
   * presence (non-empty `text`) is what flips SubmissionPane to the
   * monospace code view; paper submissions never carry it and keep the
   * photo view.
   */
  code_submission?: CodeSubmissionRecord
  /**
   * PRD-50 §FR-7 — provenance of the engine's verdict. Present only once
   * the submission-evaluated webhook (or the local fixture) landed.
   * `matched_by` "signature" is the v1 primary matcher (§9.1 — students
   * write from scratch, so `[ex:NN]` headers only exist from Fase 3 on).
   */
  code_verdict?: {
    verdict_version: number
    matched_by: "header" | "signature" | "none" | string
    evaluated_at: string
    /**
     * The language the submission was evaluated in. Drives how the
     * SubmissionPane finds block boundaries (a `def foo():` is not a
     * `FUNCION foo(...)`). Absent on rows written before the field
     * existed — those are pseudocode by construction, which is what the
     * reader defaults to.
     */
    language?: ProgrammingLanguage | string | null
    /** Material-level exercise type; per-exercise values live on `code`. */
    exercise_type?: CodingItemType | string | null
  }
}

// Mirrors the `{"text": ..., "files": [...]}` dict written by
// `core/tasks.py::_grade_codigo_submission` (and preserved by
// `core/code_grading.py` on every degrade/verdict path). Keys are
// optional because older sweeps re-wrote the dict defensively.
export interface CodeSubmissionRecord {
  text?: string
  files?: CodeSubmissionFile[]
}

// One record per uploaded file: `kind` "text" (.txt) / "docx" contributed
// code; "context" (photos, PDF scans) rides along for the professor only.
export interface CodeSubmissionFile {
  seq: number
  path: string
  kind: "text" | "docx" | "context" | string
}

export interface ProgressUpload {
  id: string
  task_id: string
  student_id: string
  original_filename: string
  content_type: string
  file_size: number
  status: ProgressUploadStatus
  created_at: string
  file_url: string
  ocr_metadata: GradingMetadata | null
  error_code: string
  // PRD-21 §FR-Action-endpoints — populated by ``create_grading_upload``
  // before dispatching the worker.
  task_run_id: string | null
}

// PRD-09: per-student detail payload mirrors. Source of truth is
// apps/backend/api/schemas.py::StudentProgressDetailOut.
export interface StudentTaskHistory {
  task_id: string
  task_name: string
  due_date: string
  material_id: string | null
  material_title: string | null
  // PRD-20 §FR-33 — exam check in-band so the student detail page
  // can decide camera-on/off per row without an extra fetch.
  material_type: MaterialType | null
  delivered: boolean | null
  grade: number | null
  status: ProgressStatus | null
  // PRD-20 v1.4 §FR-67 — in-flight upload state for this row so the
  // student detail page can render Revisar / Procesando pills + the
  // "N por revisar" badge, mirroring `ProgressCell.pending_upload`.
  // Null when no upload exists OR when the cell is already graded
  // (saved grade always takes precedence — same rule as the matrix).
  // Optional (`?:`) so existing mocks + legacy responses parse without
  // a breaking diff; the runtime treats `undefined` and `null` identically.
  pending_upload?: PendingUpload | null
  // PRD-06 §FR-21 follow-up — id of the ProgressUpload attached to
  // this row, surfaced on BOTH pending and graded rows so the student
  // detail page renders a click-to-preview affordance on the photo
  // that produced the saved grade. Mirrors `ProgressCell.upload_id`.
  // `null` for manual-entry grades + empty rows (the "skip if grade
  // was added manually" cut).
  upload_id?: string | null
  /** PRD-61 §FR-5.2 — the evaluation cut this row counts for; null when it
   *  falls outside every cut. Optional for payloads cached across the deploy. */
  cut?: number | null
}

export interface StudentActivity {
  task_id: string
  task_name: string
  kind: "graded" | "pending_grading" | "awaiting" | "not_delivered"
  grade: number | null
  updated_at: string
}

export interface StudentProgressSummary {
  total_tasks: number
  delivered_count: number
  graded_count: number
  pending_grading_count: number
  awaiting_count: number
  not_delivered_count: number
  highest_grade: number | null
  lowest_grade: number | null
  last_grade: number | null
  last_grade_at: string | null
  delta_from_first_to_last: number | null
}

export interface StudentProgressDetail {
  student: Student
  classroom: Classroom
  tasks: StudentTaskHistory[]
  recent_activity: StudentActivity[]
  summary: StudentProgressSummary
  /** PRD-61 §FR-5.2 — this student's average per cut of the classroom. */
  cuts?: StudentCutAverage[]
}

// PRD-10: home dashboard payload mirrors. Source of truth is
// apps/backend/api/schemas.py::HomeOverviewOut.
export interface HomeKpis {
  /** PRD-40 §FR-14 — distinct PEOPLE, not enrollment rows. */
  total_students: number
  /** Student-subject pairs. Optional for payloads cached across the deploy. */
  total_enrollments?: number
  total_classrooms: number
  avg_grade: number
  delivery_rate: number
  /** Distinct people at risk in ≥1 subject. */
  at_risk_count: number
  total_records: number
}

export interface HomePendientes {
  materials_in_review: number
  materials_in_progress: number
  pending_grading: number
  awaiting: number
  // PRD-41 §FR-5.1 — submissions the worker scored but couldn't
  // auto-commit. Counted over (student, task) cells, not upload rows,
  // so a student who submitted twice counts once. Distinct from
  // `pending_grading`, which is "delivered but ungraded" and includes
  // everything never photographed at all.
  grading_pending_review: number
  /**
   * The one material all that pending work belongs to, so the strip can
   * deep-link into its review queue instead of a materials list. Null
   * when the work spans several materials.
   * Mirrors `api/schemas.py::HomePendientesOut`.
   */
  grading_pending_review_material_id: string | null
}

export interface HomeClassroomChartRow {
  id: string
  name: string
  subject: string
  // Mirrors Classroom.effective_icon (PRD-30) — the resolved icon name to render.
  icon: string
  avg_grade: number
  at_risk_count: number
}

export interface HomeAtRiskRow {
  student_id: string
  /** PRD-40 — the person. Null only for rows awaiting the backfill. */
  profile_id: string | null
  name: string
  classroom_id: string
  classroom_name: string
  avg_grade: number
  /** PRD-58 §FR-5 — why the WORST subject needs attention. */
  attention_reason: AttentionReason | null
  /** How many tasks (`missed_deliveries`) or grades (`low_average`) the
   *  reason covers; null when not at risk. Optional for older payloads. */
  attention_n?: number | null
  color: "purple" | "blue" | "green" | "orange"
  initials: string
  /**
   * PRD-40 §FR-14 — every subject this person is at risk in. The row is one
   * per PERSON now; `classroom_id` / `avg_grade` describe their WORST
   * subject, which is the deep-link target. Optional so a payload cached
   * across the deploy still parses.
   */
  at_risk_subjects?: string[]
}

/** PRD-59 §FR-6 — one dated fact about today. Mirrors
 *  apps/backend/api/schemas.py::HomeAgendaItemOut. */
export interface HomeAgendaItem {
  kind: "send_today" | "due_today" | "overdue"
  material_id: string
  material_title: string
  material_type: MaterialType
  classroom_id: string
  classroom_name: string
  date: string
  pending_count: number
}

export interface HomeAgenda {
  today: string
  items: HomeAgendaItem[]
}

/** `GET /api/home/` → `adaptive_pending` (PRD-48): adaptive-level
 *  recommendations waiting on the professor. Null when there are none; the
 *  classroom is named only when they all belong to one.
 *  Mirrors apps/backend/api/schemas.py::HomeAdaptivePendingOut. */
export interface HomeAdaptivePending {
  count: number
  classroom_count: number
  classroom_id: string | null
  classroom_name: string | null
}

export interface HomeOverview {
  professor: Professor
  kpis: HomeKpis
  pendientes: HomePendientes
  agenda: HomeAgenda
  classrooms_preview: Classroom[]
  classrooms_chart: HomeClassroomChartRow[]
  classrooms_total: number
  at_risk_top: HomeAtRiskRow[]
  recent_materials: Material[]
  /** PRD-61 §FR-5.4 — optional so a payload cached across the deploy (and
   *  the demo home's sample data) still parses. */
  closing_cut?: HomeClosingCut | null
  /** PRD-48 — optional for the same deploy-window reason. */
  adaptive_pending?: HomeAdaptivePending | null
}

// PRD-26 — notifications system. `kind` is the per-event canonical
// identifier the SPA uses to pick icon + deep-link; `notification_type`
// is the legacy 5-tier enum kept for back-compat with older renderers.
export type NotificationKind =
  | "material.ready"
  | "material.generation_failed"
  | "material.scheduled_dispatched"
  | "material.scheduled_failed"
  | "material.scheduled_auto_sent"
  | "material.due_today"
  | "material.deadline_approaching"
  // PRD-42 §FR-7.1 — students who joined the classroom after the
  // material generated, so the send never reached them.
  | "material.roster_gap"
  | "material.send_failed"
  | "material.review_ready"
  | "grading.review_needed"
  | "grading.completed_auto"
  | "portal.student_uploaded"
  | "student.survey_completed"
  | "welcome"

export type NotificationTier = "info" | "alerta" | "exito" | "tarea" | "alumno_subio"

export interface Notification {
  id: string
  notification_type: NotificationTier | string
  kind: NotificationKind | string
  title: string
  message: string
  link: string
  payload: Record<string, unknown>
  read: boolean
  read_at: string | null
  archived_at: string | null
  created_at: string
}

// ── Unified student profile (PRD-40) ─────────────────────────────────
// Mirrors apps/backend/api/schemas.py::StudentProfileOverviewOut.

export interface StudentNote {
  id: string
  body: string
  pinned: boolean
  /** Provenance only — the subject the note was written under, if any. */
  classroom_id: string | null
  classroom_name: string | null
  created_at: string
  updated_at: string
}

export interface ProfileEnrollment {
  /** The enrollment row — what the gradebook deep links still key on. */
  student_id: string
  classroom_id: string
  classroom_name: string
  education_level: EducationLevel
  subject_name: string
  icon: string
  avg_grade: number
  is_at_risk: boolean
  attention_reason: AttentionReason | null
  /** How many tasks (`missed_deliveries`) or grades (`low_average`) the
   *  reason covers; null when not at risk. Optional for older payloads. */
  attention_n?: number | null
  delivered_count: number
  total_tasks: number
  delivery_rate: number
  pending_grading_count: number
  not_delivered_count: number
  /** The group's average for THIS subject — never a cross-subject figure. */
  classroom_avg_grade: number
  survey_status: SurveyStatus | string
  adaptive_level: Omit<
    AdaptiveLevelStateSummary,
    "student_name" | "mode" | "latest_resolution"
  > | null
  tasks: StudentTaskHistory[]
  /** PRD-61 §FR-5.2 — this student's average per cut of THIS enrollment's
   *  classroom. Empty or absent when the school has no calendar. */
  cuts?: StudentCutAverage[]
}

export interface StudentProfileOverview {
  id: string
  name: string
  email: string
  matricula: string
  persona: Record<string, unknown> | null
  initials: string
  color: string
  /** Subjects the student is at risk in. Empty = not at risk anywhere. */
  at_risk_subjects: string[]
  /** Informational only — never a signal, never colored (PRD-40 §FR-11). */
  overall_avg_grade: number
  enrollments: ProfileEnrollment[]
  notes: StudentNote[]
}

/** Mirrors api/schemas.py::StudentProfileIdentityOut (PRD-40 §FR-19). */
export interface StudentProfileIdentity {
  id: string
  name: string
  email: string
  matricula: string
  persona: Record<string, unknown> | null
  /** Enrollment rows the edit propagated onto — every classroom, not only the caller's. */
  enrollments_updated: number
}

// ──────────────────────────────────────────────────────────
// Review queue (PRD-41)
// Mirrors api/schemas.py::ReviewQueue*Out.
// ──────────────────────────────────────────────────────────

export interface ReviewQueueStudent {
  id: string
  name: string
}

/**
 * One submission awaiting confirmation.
 *
 * An item is the *newest* upload for its (student, task) pair, listed
 * only while that newest row is awaiting review — the same collapse rule
 * the gradebook applies, so the queue can never disagree with the cell it
 * came from. Fields read out of `ocr_metadata` are null on rows the
 * worker wrote before that key existed; render "unknown", don't assume 0.
 */
export interface ReviewQueueItem {
  upload_id: string
  student: ReviewQueueStudent
  task_id: string
  draft_score: number | null
  questions_total: number | null
  questions_correct: number | null
  needs_review_count: number | null
  rotated_image_count: number | null
  /**
   * PRD-43 — why the automatic pass produced nothing (`vlm_unavailable`,
   * `answer_key_missing`). Present on the LIST so the reasons panel can
   * explain a degraded row before its detail fetch lands.
   * Mirrors `api/schemas.py::ReviewQueueItemOut.error_code`.
   */
  error_code: string | null
  /**
   * PRD-67 — what the reader's output could not vouch for. On the LIST for
   * the same reason as `error_code`, and the queue already sorts these
   * first. Mirrors `api/schemas.py::ReviewQueueItemOut.review_flags`.
   */
  review_flags: string[]
  created_at: string
  /** Submitted after the deadline. The deadline day itself is on time. */
  late: boolean
  files_count: number
  /**
   * PRD-68 §FR-8.2 — a portal submission is a version; `submitted_at` is
   * when the student sent it. Mirrors `api/schemas.py::ReviewQueueItemOut`.
   * Optional for the deploy window.
   */
  source?: ProgressUploadSource
  version?: number | null
  submitted_at?: string | null
  /**
   * PRD-73 §FR-5 — the last Mexico City day (`YYYY-MM-DD`) of the cell's
   * reopened window, active or past. Optional for the deploy window.
   */
  submission_reopened_through?: string | null
}

export interface ReviewQueueSummary {
  assignments_total: number
  auto_graded: number
  pending_review: number
  /** Queued + running folded together: nothing for the professor to do yet. */
  processing: number
  failed: number
  not_submitted: number
  deadline: string | null
  deadline_passed: boolean
}

export interface ReviewQueue {
  items: ReviewQueueItem[]
  summary: ReviewQueueSummary
  page: number
  page_size: number
  total: number
}
